// Revenue calculation utilities for the NGAP simulator
import { NgapAct } from '../types/ngap';
import { TimePeriod, RevenueCalculation, CustomPeriodMultipliers, CustomPointValues, BalneotherapyType, BALNEOTHERAPY_TARIFFS } from '../features/simulator/types';
import { PERIOD_MULTIPLIERS } from '../features/simulator/types';
import { calculateTarif, getTariffEvolution, POINT_VALUE_METROPOLE, POINT_VALUE_DROM, OLD_POINT_VALUE_METROPOLE, OLD_POINT_VALUE_DROM, getDisplacementValue } from './pricingUtils';

// Extended revenue calculation interface for evolution display
export interface RevenueEvolution {
  original: {
    value: number;
    label: string;
    date: Date;
  };
  current: {
    value: number;
    label: string;
    date: Date;
  };
  janv2026?: {
    value: number;
    label: string;
    date: Date;
  };
  july2025?: {
    value: number;
    label: string;
    date: Date;
  };
  may2026?: {
    value: number;
    label: string;
    date: Date;
  };
  september2026?: {
    value: number;
    label: string;
    date: Date;
  };
  july2027?: {
    value: number;
    label: string;
    date: Date;
  };
}

/**
 * Gets the balneotherapy supplement value for a given type and period
 * @param balneotherapy - The type of balneotherapy ('none', 'pool', 'basin')
 * @param period - The period ('original', 'current', or any 2026+ period)
 * @returns The supplement value in euros
 */
export function getBalneotherapyValue(
  balneotherapy: BalneotherapyType,
  period: 'original' | 'current' | 'janv2026' | 'may2026' | 'sept2026' | 'july2027'
): number {
  if (balneotherapy === 'none') return 0;

  // For 'original' and 'current' periods, use 2025 tariffs
  // For all 2026 and 2027 periods, use 2026 tariffs
  const useTariff2026 = period !== 'original' && period !== 'current';
  const tariffs = useTariff2026 ? BALNEOTHERAPY_TARIFFS[2026] : BALNEOTHERAPY_TARIFFS[2025];

  return balneotherapy === 'pool' ? tariffs.pool : tariffs.basin;
}

/**
 * Converts a quantity from one time period to another
 * @param quantity - The quantity to convert
 * @param fromPeriod - The source time period
 * @param toPeriod - The target time period
 * @param customMultipliers - Custom period multipliers (optional)
 * @returns The converted quantity, rounded to 2 decimal places
 */
export function convertQuantity(
  quantity: number,
  fromPeriod: TimePeriod,
  toPeriod: TimePeriod,
  customMultipliers?: CustomPeriodMultipliers
): number {
  if (fromPeriod === toPeriod || quantity === 0) {
    return quantity;
  }

  // Use custom multipliers if provided, otherwise use defaults
  const multipliers = customMultipliers || PERIOD_MULTIPLIERS;

  // Convert to daily equivalent first
  const dailyEquivalent = quantity / multipliers[fromPeriod];
  
  // Convert to target period
  const convertedQuantity = dailyEquivalent * multipliers[toPeriod];
  
  return convertedQuantity;
}

/**
 * Calculates complete revenue evolution for a single act across all revaluation periods
 * @param act - The NGAP act
 * @param dailyQuantity - Number of times the act is performed per day (stored value)
 * @param summaryDisplayPeriod - The period for which to calculate total revenue
 * @param isDrom - Whether to use DROM pricing (default: false for Metropole)
 * @param homeVisitPercentage - Percentage of home visits (0-100)
 * @param customMultipliers - Custom period multipliers (optional)
 * @param customPointValues - Custom point values (optional)
 * @param balneotherapy - Type of balneotherapy supplement (default: 'none')
 * @returns Complete revenue evolution object with all revaluation periods
 */
export function calculateActRevenueEvolution(
  act: NgapAct,
  dailyQuantity: number,
  summaryDisplayPeriod: TimePeriod,
  isDrom: boolean = false,
  homeVisitPercentage: number = 0,
  customMultipliers?: CustomPeriodMultipliers,
  customPointValues?: CustomPointValues,
  balneotherapy: BalneotherapyType = 'none'
): RevenueEvolution {
  // Validate inputs
  if (!act || dailyQuantity <= 0) {
    const emptyValue = { value: 0, label: 'N/A', date: new Date() };
    return {
      original: emptyValue,
      current: emptyValue
    };
  }

  // Convert daily quantity to total quantity for the summary period
  const multipliers = customMultipliers || PERIOD_MULTIPLIERS;
  const totalQuantity = dailyQuantity * multipliers[summaryDisplayPeriod];

  if (totalQuantity <= 0) {
    const emptyValue = { value: 0, label: 'N/A', date: new Date() };
    return {
      original: emptyValue,
      current: emptyValue
    };
  }

  // Get tariff evolution for this act
  const tariffEvolution = getTariffEvolution(act, isDrom, customPointValues);

  // Calculate displacement indemnity revenue if home visits > 0
  const displacementRevenue = homeVisitPercentage > 0 ?
    getDisplacementValue(act.indemnite_deplacement) * (homeVisitPercentage / 100) * totalQuantity : 0;

  // Calculate balneotherapy supplements for each period
  const balneotherapyOriginal = getBalneotherapyValue(balneotherapy, 'original') * totalQuantity;
  const balneotherapyCurrent = getBalneotherapyValue(balneotherapy, 'current') * totalQuantity;
  const balneotherapyJanv2026 = getBalneotherapyValue(balneotherapy, 'janv2026') * totalQuantity;
  const balneotherapyMay2026 = getBalneotherapyValue(balneotherapy, 'may2026') * totalQuantity;
  const balneotherapySept2026 = getBalneotherapyValue(balneotherapy, 'sept2026') * totalQuantity;
  const balneotherapyJuly2027 = getBalneotherapyValue(balneotherapy, 'july2027') * totalQuantity;

  // Use custom point values if provided, otherwise use defaults
  const pointValue = isDrom ?
    (customPointValues?.drom || POINT_VALUE_DROM) :
    (customPointValues?.metropole || POINT_VALUE_METROPOLE);

  // Calculate revenue for each period
  const evolution: RevenueEvolution = {
    original: {
      value: (() => {
        let originalCotationValue: number;
        let originalPointValue: number;

        if (act.old_cotation_valeur) {
          // Si old_cotation_valeur existe, l'utiliser avec les anciennes valeurs de point
          originalCotationValue = parseFloat(act.old_cotation_valeur.replace(',', '.'));
          originalPointValue = isDrom ? OLD_POINT_VALUE_DROM : OLD_POINT_VALUE_METROPOLE;
        } else {
          // Sinon, utiliser cotation_valeur avec les valeurs de point actuelles
          originalCotationValue = parseFloat(act.cotation_valeur.replace(',', '.'));
          originalPointValue = pointValue; // Utilise les valeurs personnalisées ou par défaut
        }

        return Math.round((originalCotationValue * originalPointValue * totalQuantity + balneotherapyOriginal) * 100) / 100;
      })(),
      label: 'Valeur d\'origine',
      date: new Date('2024-01-01') // Approximate date for original values
    },
    current: {
      value: Math.round((tariffEvolution.current.value * totalQuantity + displacementRevenue + balneotherapyCurrent) * 100) / 100,
      label: tariffEvolution.current.label,
      date: tariffEvolution.current.date
    }
  };

  // Add future revalorizations if they exist
  if (tariffEvolution.janv2026) {
    evolution.janv2026 = {
      value: Math.round((tariffEvolution.janv2026.value * totalQuantity + displacementRevenue + balneotherapyJanv2026) * 100) / 100,
      label: tariffEvolution.janv2026.label,
      date: tariffEvolution.janv2026.date
    };
  }

  if (tariffEvolution.july2025) {
    evolution.july2025 = {
      value: Math.round((tariffEvolution.july2025.value * totalQuantity + displacementRevenue) * 100) / 100,
      label: tariffEvolution.july2025.label,
      date: tariffEvolution.july2025.date
    };
  }

  if (tariffEvolution.may2026) {
    evolution.may2026 = {
      value: Math.round((tariffEvolution.may2026.value * totalQuantity + displacementRevenue + balneotherapyMay2026) * 100) / 100,
      label: tariffEvolution.may2026.label,
      date: tariffEvolution.may2026.date
    };
  }

  if (tariffEvolution.september2026) {
    evolution.september2026 = {
      value: Math.round((tariffEvolution.september2026.value * totalQuantity + displacementRevenue + balneotherapySept2026) * 100) / 100,
      label: tariffEvolution.september2026.label,
      date: tariffEvolution.september2026.date
    };
  }

  if (tariffEvolution.july2027) {
    evolution.july2027 = {
      value: Math.round((tariffEvolution.july2027.value * totalQuantity + displacementRevenue + balneotherapyJuly2027) * 100) / 100,
      label: tariffEvolution.july2027.label,
      date: tariffEvolution.july2027.date
    };
  }

  return evolution;
}

/**
 * Converts a daily quantity to display quantity for a specific period
 * @param dailyQuantity - The daily equivalent quantity
 * @param displayPeriod - The period to display the quantity for
 * @param customMultipliers - Custom period multipliers (optional)
 * @returns The quantity to display for the specified period (rounded to natural integer)
 */
export function convertDailyToDisplayQuantity(
  dailyQuantity: number,
  displayPeriod: TimePeriod,
  customMultipliers?: CustomPeriodMultipliers
): number {
  const convertedValue = convertQuantity(dailyQuantity, 'day', displayPeriod, customMultipliers);
  // Round to natural integer for display (minimum 1)
  return Math.max(1, Math.round(convertedValue));
}

/**
 * Converts a display quantity to daily equivalent
 * @param displayQuantity - The quantity in the display period
 * @param displayPeriod - The period the quantity is expressed in
 * @param customMultipliers - Custom period multipliers (optional)
 * @returns The daily equivalent quantity
 */
export function convertDisplayToDailyQuantity(
  displayQuantity: number,
  displayPeriod: TimePeriod,
  customMultipliers?: CustomPeriodMultipliers
): number {
  return convertQuantity(displayQuantity, displayPeriod, 'day', customMultipliers);
}

/**
 * Calculates revenue for a single act based on daily quantity and summary period
 * @param act - The NGAP act
 * @param dailyQuantity - Number of times the act is performed per day (stored value)
 * @param summaryDisplayPeriod - The period for which to calculate total revenue
 * @param isDrom - Whether to use DROM pricing (default: false for Metropole)
 * @param customMultipliers - Custom period multipliers (optional)
 * @returns Revenue calculation object with current and future values
 */
export function calculateActRevenue(
  act: NgapAct,
  dailyQuantity: number,
  summaryDisplayPeriod: TimePeriod,
  isDrom: boolean = false,
  customMultipliers?: CustomPeriodMultipliers,
  customPointValues?: CustomPointValues
): RevenueCalculation {
  // Validate inputs
  if (!act || dailyQuantity <= 0) {
    return {
      currentRevenue: 0,
      futureRevenue: 0,
      difference: 0,
      percentageChange: 0
    };
  }

  // Convert daily quantity to total quantity for the summary period
  const multipliers = customMultipliers || PERIOD_MULTIPLIERS;
  const totalQuantity = dailyQuantity * multipliers[summaryDisplayPeriod];

  // Validate that we have a positive total quantity
  if (totalQuantity <= 0) {
    return { currentRevenue: 0, futureRevenue: 0, difference: 0, percentageChange: 0 };
  }
  
  const currentTariff = calculateTarif(act.cotation_valeur, isDrom, act.cotation, new Date(), customPointValues);
  const currentRevenue = currentTariff * totalQuantity;

  // Calculate future revenue - determine which revalorization applies
  let futureRevenue = currentRevenue; // Default to current if no revalorization
  
  // Check for the most recent applicable revalorization
  const now = new Date();
  
  if (act.revalo_july_2027 && act.cotation_valeur_july_2027 && now >= new Date('2027-07-01')) {
    const futureTariff = calculateTarif(act.cotation_valeur_july_2027, isDrom, act.cotation, new Date(), customPointValues);
    futureRevenue = futureTariff * totalQuantity;
  } else if (act.revalo_sept_2026 && act.cotation_valeur_sept_2026 && now >= new Date('2026-09-01')) {
    const futureTariff = calculateTarif(act.cotation_valeur_sept_2026, isDrom, act.cotation, new Date(), customPointValues);
    futureRevenue = futureTariff * totalQuantity;
  } else if (act.revalo_may_2026 && act.cotation_valeur_may_2026 && now >= new Date('2026-05-28')) {
    const futureTariff = calculateTarif(act.cotation_valeur_may_2026, isDrom, act.cotation, new Date(), customPointValues);
    futureRevenue = futureTariff * totalQuantity;
  } else if (act.revalo_janv_2026 && act.cotation_valeur_janv_2026) {
    // Use January 2026 revalorization as the "future" reference
    const futureTariff = calculateTarif(act.cotation_valeur_janv_2026, isDrom, act.cotation, new Date(), customPointValues);
    futureRevenue = futureTariff * totalQuantity;
  }

  // Calculate difference and percentage
  const difference = futureRevenue - currentRevenue;
  const percentageChange = currentRevenue > 0 ? (difference / currentRevenue) * 100 : 0;

  return {
    currentRevenue: Math.round(currentRevenue * 100) / 100,
    futureRevenue: Math.round(futureRevenue * 100) / 100,
    difference: Math.round(difference * 100) / 100,
    percentageChange: Math.round(percentageChange * 100) / 100
  };
}

/**
 * Formats a revenue value for display
 * @param value - The revenue value to format
 * @returns Formatted string with 2 decimal places
 */
export function formatRevenue(value: number): string {
  return value.toFixed(2);
}

/**
 * Determines the color class for displaying revenue differences
 * @param difference - The revenue difference
 * @param darkMode - Whether dark mode is active
 * @returns CSS class string for styling
 */
export function getRevenueChangeColor(difference: number, darkMode: boolean = false): string {
  if (difference > 0) {
    return darkMode ? 'text-green-400' : 'text-green-600';
  } else if (difference < 0) {
    return darkMode ? 'text-red-400' : 'text-red-600';
  }
  return darkMode ? 'text-gray-400' : 'text-gray-600';
}

/**
 * Gets the appropriate icon for revenue change
 * @param difference - The revenue difference
 * @returns Icon name or emoji
 */
export function getRevenueChangeIcon(difference: number): string {
  if (difference > 0) return '📈';
  if (difference < 0) return '📉';
  return '➡️';
}